import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
    getAccounts,
    getAccount,
    getBalances,
    getParty,
    getStatements,
    getStatement,
    getTransactions,
    downloadStatement
} from '../demoBffClientInstance';

const Redirect: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const consentId = searchParams.get('cid');

    const [loading, setLoading] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('accounts');

    // Data states
    const [accounts, setAccounts] = useState<any[]>([]);
    const [selectedAccountId, setSelectedAccountId] = useState<string>('');
    const [accountDetails, setAccountDetails] = useState<any>(null);
    const [balances, setBalances] = useState<any[]>([]);
    const [party, setParty] = useState<any>(null);
    const [statements, setStatements] = useState<any[]>([]);
    const [selectedStatementId, setSelectedStatementId] = useState<string>('');
    const [statementDetails, setStatementDetails] = useState<any>(null);
    const [transactions, setTransactions] = useState<any[]>([]);

    const fetchAccounts = useCallback(async () => {
        if (!consentId) return;

        setLoading(true);
        setError('');

        try {
            console.log('Fetching accounts for consent:', consentId);
            const accountsPage = await getAccounts(consentId);
            console.log('Accounts page:', accountsPage);
            setAccounts(accountsPage?.accounts || []);
        } catch (error: any) {
            console.error('Error fetching accounts:', error);
            setError('Failed to fetch accounts: ' + (error?.message || 'Unknown error'));
        } finally {
            setLoading(false);
        }
    }, [consentId]);

    const fetchAccountDetails = useCallback(async () => {
        if (!selectedAccountId || !consentId) return;

        setLoading(true);
        setError('');

        try {
            console.log('Fetching account details for:', selectedAccountId);
            const details = await getAccount(selectedAccountId, consentId);
            console.log('Account details:', details);
            setAccountDetails(details);
        } catch (error: any) {
            console.error('Error fetching account details:', error);
            setError('Failed to fetch account details: ' + (error?.message || 'Unknown error'));
        } finally {
            setLoading(false);
        }
    }, [selectedAccountId, consentId]);

    // Fetch accounts on component mount
    useEffect(() => {
        if (consentId) {
            fetchAccounts();
        } else {
            setError('No consent ID provided');
        }
    }, [consentId, fetchAccounts]);

    // Fetch account details when an account is selected
    useEffect(() => {
        if (selectedAccountId && consentId) {
            fetchAccountDetails();
        }
    }, [selectedAccountId, consentId, fetchAccountDetails]);

    const fetchBalances = useCallback(async () => {
        if (!selectedAccountId || !consentId) return;

        setLoading(true);
        setError('');

        try {
            console.log('Fetching balances for account:', selectedAccountId);
            const balancesPage = await getBalances(selectedAccountId, consentId);
            console.log('Balances page:', balancesPage);
            setBalances(balancesPage?.balances || []);
        } catch (error: any) {
            console.error('Error fetching balances:', error);
            setError('Failed to fetch balances: ' + (error?.message || 'Unknown error'));
        } finally {
            setLoading(false);
        }
    }, [selectedAccountId, consentId]);

    const fetchParty = useCallback(async () => {
        if (!selectedAccountId || !consentId) return;

        setLoading(true);
        setError('');

        try {
            console.log('Fetching party for account:', selectedAccountId);
            const partyData = await getParty(selectedAccountId, consentId);
            console.log('Party:', partyData);
            setParty(partyData);
        } catch (error: any) {
            console.error('Error fetching party:', error);
            setError('Failed to fetch party: ' + (error?.message || 'Unknown error'));
        } finally {
            setLoading(false);
        }
    }, [selectedAccountId, consentId]);

    const fetchStatements = useCallback(async () => {
        if (!selectedAccountId || !consentId) return;

        setLoading(true);
        setError('');

        try {
            const now = new Date();
            const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());

            console.log('Fetching statements for account:', selectedAccountId);
            const statementsPage = await getStatements(
                selectedAccountId,
                consentId,
                sixMonthsAgo.toISOString(),
                now.toISOString()
            );
            console.log('Statements page:', statementsPage);
            setStatements(statementsPage?.statements || []);
        } catch (error: any) {
            console.error('Error fetching statements:', error);
            setError('Failed to fetch statements: ' + (error?.message || 'Unknown error'));
        } finally {
            setLoading(false);
        }
    }, [selectedAccountId, consentId]);

    const fetchStatementDetails = useCallback(async () => {
        if (!selectedStatementId || !selectedAccountId || !consentId) return;

        setLoading(true);
        setError('');

        try {
            console.log('Fetching statement details for:', selectedStatementId);
            const details = await getStatement(selectedAccountId, selectedStatementId, consentId);
            console.log('Statement details:', details);
            setStatementDetails(details);
        } catch (error: any) {
            console.error('Error fetching statement details:', error);
            setError('Failed to fetch statement details: ' + (error?.message || 'Unknown error'));
        } finally {
            setLoading(false);
        }
    }, [selectedStatementId, selectedAccountId, consentId]);

    // Fetch statement details when a statement is selected
    useEffect(() => {
        if (selectedStatementId && selectedAccountId && consentId) {
            fetchStatementDetails();
        }
    }, [selectedStatementId, selectedAccountId, consentId, fetchStatementDetails]);

    const handleDownloadStatement = async () => {
        if (!selectedStatementId || !selectedAccountId || !consentId) return;

        setDownloadLoading(true);

        try {
            console.log('Downloading statement file for:', selectedStatementId);
            const result = await downloadStatement(selectedAccountId, selectedStatementId, consentId);
            console.log('Statement file downloaded successfully', result);

            if (result.type === 'text') {
                // Handle plain text - create a blob from the text
                const textBlob = new Blob([result.data], { type: 'text/plain' });
                const url = window.URL.createObjectURL(textBlob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `statement-${selectedStatementId}.txt`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } else {
                // Handle PDF blob
                const url = window.URL.createObjectURL(result.data);
                const link = document.createElement('a');
                link.href = url;
                link.download = `statement-${selectedStatementId}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            }
        } catch (error: any) {
            console.error('Error downloading statement file:', error);
            setError('Failed to download statement file: ' + (error?.message || 'Unknown error'));
        } finally {
            setDownloadLoading(false);
        }
    };

    const fetchTransactions = useCallback(async () => {
        if (!selectedAccountId || !consentId) return;

        setLoading(true);
        setError('');

        try {
            const now = new Date();
            const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
            const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());

            console.log('Fetching transactions for account:', selectedAccountId);
            const transactionsPage = await getTransactions(
                selectedAccountId,
                consentId,
                sixMonthsAgo.toISOString(),
                endOfToday.toISOString()
            );
            console.log('Transactions page:', transactionsPage);
            setTransactions(transactionsPage?.transactions || []);
        } catch (error: any) {
            console.error('Error fetching transactions:', error);
            setError('Failed to fetch transactions: ' + (error?.message || 'Unknown error'));
        } finally {
            setLoading(false);
        }
    }, [selectedAccountId, consentId]);

    const handleTabClick = (tab: string) => {
        setActiveTab(tab);
        setError('');

        // Fetch data based on selected tab
        switch (tab) {
            case 'balances':
                if (balances.length === 0) fetchBalances();
                break;
            case 'party':
                if (!party) fetchParty();
                break;
            case 'statements':
                if (statements.length === 0) fetchStatements();
                break;
            case 'transactions':
                if (transactions.length === 0) fetchTransactions();
                break;
        }
    };

    const handleAccountSelect = (accountId: string) => {
        setSelectedAccountId(accountId);
        // Reset dependent data
        setBalances([]);
        setParty(null);
        setStatements([]);
        setSelectedStatementId('');
        setStatementDetails(null);
        setTransactions([]);
    };

    const handleStatementSelect = (statementId: string) => {
        setSelectedStatementId(statementId);
    };

    const handleNewApplication = () => {
        // Clear all data and navigate to home
        localStorage.removeItem('consentId');
        navigate('/');
    };

    const renderAccountsTab = () => (
        <div className="accounts-content">
            {accounts.length === 0 ? (
                <p>No accounts found</p>
            ) : (
                <div className="accounts-list">
                    <h3>Select an Account:</h3>
                    {accounts.map((account) => (
                        <label key={account.accountId} className="account-option">
                            <input
                                type="radio"
                                name="account"
                                value={account.accountId}
                                checked={selectedAccountId === account.accountId}
                                onChange={() => handleAccountSelect(account.accountId)}
                            />
                            <div className="account-info">
                                <span className="account-number">{account.details?.number || account.accountId}</span>
                                <span className="account-name">{account.name || account.details?.name || ''}</span>
                                <span className="account-currency">{account.currency || ''}</span>
                            </div>
                        </label>
                    ))}
                </div>
            )}

            {selectedAccountId && accountDetails && (
                <div className="account-details">
                    <h3>Account Details:</h3>
                    <div className="details-content">
                        <div className="detail-row">
                            <span className="detail-label">Account ID:</span>
                            <span className="detail-value">{accountDetails.accountId}</span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">Currency:</span>
                            <span className="detail-value">{accountDetails.currency || ''}</span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">Name:</span>
                            <span className="detail-value">{accountDetails.name || ''}</span>
                        </div>
                        {accountDetails.details && (
                            <>
                                <div className="detail-row">
                                    <span className="detail-label">Account Number:</span>
                                    <span className="detail-value">{accountDetails.details.number || ''}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Account Name:</span>
                                    <span className="detail-value">{accountDetails.details.name || ''}</span>
                                </div>
                                {accountDetails.details.alternativeId && (
                                    <div className="detail-row">
                                        <span className="detail-label">Alternative ID:</span>
                                        <span className="detail-value">{accountDetails.details.alternativeId}</span>
                                    </div>
                                )}
                            </>
                        )}
                        {accountDetails.status && (
                            <div className="detail-row">
                                <span className="detail-label">Status:</span>
                                <span className="detail-value">{accountDetails.status}</span>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );

    const renderBalancesTab = () => (
        <div className="balances-content">
            {!selectedAccountId ? (
                <p>Please select an account first</p>
            ) : loading ? (
                <p>Loading balances...</p>
            ) : balances && balances.length > 0 ? (
                <div>
                    <h3>Account Balances:</h3>
                    <div className="table-container">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Type</th>
                                    <th>Direction</th>
                                    <th>Amount</th>
                                    <th>Currency</th>
                                    <th>Last Updated</th>
                                </tr>
                            </thead>
                            <tbody>
                                {balances.map((balance: any, index: number) => (
                                    <tr key={index}>
                                        <td>{balance.type || ''}</td>
                                        <td>{balance.direction || ''}</td>
                                        <td className={`amount ${balance.direction === 'in' ? 'credit' : 'debit'}`}>
                                            {balance.amount?.amount || '0.00'}
                                        </td>
                                        <td>{balance.amount?.currency || ''}</td>
                                        <td>{balance.dateTime ? new Date(balance.dateTime).toLocaleString() : ''}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <p>No balance data available</p>
            )}
        </div>
    );

    const renderPartyTab = () => (
        <div className="party-content">
            {!selectedAccountId ? (
                <p>Please select an account first</p>
            ) : loading ? (
                <p>Loading party information...</p>
            ) : party ? (
                <div>
                    <h3>Party Information:</h3>
                    <div className="details-content">
                        <div className="detail-row">
                            <span className="detail-label">Party ID:</span>
                            <span className="detail-value">{party.partyId || ''}</span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">Name:</span>
                            <span className="detail-value">{party.name || ''}</span>
                        </div>
                        {party.partyType && (
                            <div className="detail-row">
                                <span className="detail-label">Party Type:</span>
                                <span className="detail-value">{party.partyType}</span>
                            </div>
                        )}
                        {party.emailAddress && (
                            <div className="detail-row">
                                <span className="detail-label">Email:</span>
                                <span className="detail-value">{party.emailAddress}</span>
                            </div>
                        )}
                        {party.phone && (
                            <div className="detail-row">
                                <span className="detail-label">Phone:</span>
                                <span className="detail-value">{party.phone}</span>
                            </div>
                        )}
                        {party.address && (
                            <div className="detail-row">
                                <span className="detail-label">Address:</span>
                                <span className="detail-value">
                                    {[party.address.streetAddress,
                                      party.address.city,
                                      party.address.postCode,
                                      party.address.country].filter(Boolean).join(', ')}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <p>No party data available</p>
            )}
        </div>
    );

    const renderStatementsTab = () => (
        <div className="statements-content">
            {!selectedAccountId ? (
                <p>Please select an account first</p>
            ) : loading ? (
                <p>Loading statements...</p>
            ) : statements.length > 0 ? (
                <div>
                    <h3>Select a Statement (Last 6 months):</h3>
                    <div className="statements-list">
                        {statements.map((statement: any, index: number) => (
                            <label key={statement.statementId || index} className="statement-option">
                                <input
                                    type="radio"
                                    name="statement"
                                    value={statement.statementId}
                                    checked={selectedStatementId === statement.statementId}
                                    onChange={() => handleStatementSelect(statement.statementId)}
                                />
                                <div className="statement-info">
                                    <span className="statement-id">{statement.statementId || ''}</span>
                                    <span className="statement-type">{statement.type || ''}</span>
                                    <span className="statement-date">
                                        {statement.date ? new Date(statement.date).toLocaleDateString() : ''}
                                    </span>
                                    <span className="statement-period">
                                        {statement.period?.from && statement.period?.to
                                            ? `${new Date(statement.period.from).toLocaleDateString()} - ${new Date(statement.period.to).toLocaleDateString()}`
                                            : ''}
                                    </span>
                                </div>
                            </label>
                        ))}
                    </div>

                    {selectedStatementId && statementDetails && (
                        <div className="statement-details">
                            <div className="statement-details-header">
                                <h3>Statement Details:</h3>
                                <button
                                    onClick={handleDownloadStatement}
                                    className="download-statement-btn"
                                    disabled={downloadLoading}
                                >
                                    {downloadLoading ? 'Downloading...' : 'Download Statement'}
                                </button>
                            </div>
                            <div className="details-content">
                                <div className="detail-row">
                                    <span className="detail-label">Statement ID:</span>
                                    <span className="detail-value">{statementDetails.statementId}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Type:</span>
                                    <span className="detail-value">{statementDetails.type || ''}</span>
                                </div>
                                {statementDetails.date && (
                                    <div className="detail-row">
                                        <span className="detail-label">Statement Date:</span>
                                        <span className="detail-value">{new Date(statementDetails.date).toLocaleDateString()}</span>
                                    </div>
                                )}
                                {statementDetails.period && (
                                    <>
                                        <div className="detail-row">
                                            <span className="detail-label">Period From:</span>
                                            <span className="detail-value">
                                                {statementDetails.period.from ? new Date(statementDetails.period.from).toLocaleDateString() : ''}
                                            </span>
                                        </div>
                                        <div className="detail-row">
                                            <span className="detail-label">Period To:</span>
                                            <span className="detail-value">
                                                {statementDetails.period.to ? new Date(statementDetails.period.to).toLocaleDateString() : ''}
                                            </span>
                                        </div>
                                    </>
                                )}
                                {statementDetails.description && (
                                    <div className="detail-row">
                                        <span className="detail-label">Description:</span>
                                        <span className="detail-value">{statementDetails.description}</span>
                                    </div>
                                )}
                                {statementDetails.url && (
                                    <div className="detail-row">
                                        <span className="detail-label">Statement URL:</span>
                                        <span className="detail-value">
                                            <a href={statementDetails.url} target="_blank" rel="noopener noreferrer">
                                                View Statement
                                            </a>
                                        </span>
                                    </div>
                                )}
                                {statementDetails.lines && statementDetails.lines.length > 0 && (
                                    <div className="detail-row">
                                        <span className="detail-label">Statement Lines:</span>
                                        <div className="detail-value">
                                            <div className="table-container">
                                                <table className="data-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Date</th>
                                                            <th>Description</th>
                                                            <th>Debit</th>
                                                            <th>Credit</th>
                                                            <th>Balance</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {statementDetails.lines.map((line: any, idx: number) => (
                                                            <tr key={idx}>
                                                                <td>{line.dateTime ? new Date(line.dateTime).toLocaleDateString() : ''}</td>
                                                                <td>{line.description || ''}</td>
                                                                <td className="amount debit">
                                                                    {line.direction === 'out' && line.amount
                                                                        ? `${line.amount.currency || ''} ${line.amount.amount || ''}`
                                                                        : ''}
                                                                </td>
                                                                <td className="amount credit">
                                                                    {line.direction === 'in' && line.amount
                                                                        ? `${line.amount.currency || ''} ${line.amount.amount || ''}`
                                                                        : ''}
                                                                </td>
                                                                <td className="amount">
                                                                    {line.balance?.amount
                                                                        ? `${line.balance.amount.currency || ''} ${line.balance.amount.amount || ''}`
                                                                        : ''}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <p>No statements available</p>
            )}
        </div>
    );

    const renderTransactionsTab = () => (
        <div className="transactions-content">
            {!selectedAccountId ? (
                <p>Please select an account first</p>
            ) : loading ? (
                <p>Loading transactions...</p>
            ) : transactions.length > 0 ? (
                <div>
                    <h3>Transactions (Last 6 months):</h3>
                    <div className="table-container">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>From</th>
                                    <th>To</th>
                                    <th>Particulars</th>
                                    <th>Code</th>
                                    <th>Reference</th>
                                    <th>Debit</th>
                                    <th>Credit</th>
                                    <th>Balance</th>
                                </tr>
                            </thead>
                            <tbody>
                                {transactions.map((transaction: any, index: number) => (
                                    <tr key={transaction.transactionId || index}>
                                        <td>{transaction.dateTime ? new Date(transaction.dateTime).toLocaleDateString() : ''}</td>
                                        <td>{transaction.reference?.fromParty?.name || ''}</td>
                                        <td>{transaction.reference?.toParty?.name || ''}</td>
                                        <td>
                                            {transaction.direction === 'in'
                                                ? transaction.reference?.toParty?.reference?.particulars || ''
                                                : transaction.reference?.fromParty?.reference?.particulars || ''}
                                        </td>
                                        <td>
                                            {transaction.direction === 'in'
                                                ? transaction.reference?.toParty?.reference?.code || ''
                                                : transaction.reference?.fromParty?.reference?.code || ''}
                                        </td>
                                        <td>
                                            {transaction.direction === 'in'
                                                ? transaction.reference?.toParty?.reference?.reference || ''
                                                : transaction.reference?.fromParty?.reference?.reference || ''}
                                        </td>
                                        <td className="amount debit">
                                            {transaction.direction === 'out' ?
                                                `${transaction.amount?.currency || ''} ${transaction.amount?.amount || ''}` : ''}
                                        </td>
                                        <td className="amount credit">
                                            {transaction.direction === 'in' ?
                                                `${transaction.amount?.currency || ''} ${transaction.amount?.amount || ''}` : ''}
                                        </td>
                                        <td className="amount">
                                            {transaction.balance?.amount ?
                                                `${transaction.balance.amount.currency || ''} ${transaction.balance.amount.amount || ''}` : ''}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <p>No transactions available</p>
            )}
        </div>
    );

    return (
        <div className="redirect-container">
            <div className="redirect-header">
                <h1>Account Information</h1>
                <button onClick={handleNewApplication} className="new-app-btn">
                    New Application / Reset
                </button>
            </div>

            {error && (
                <div className="error-message">
                    {error}
                    <div className="info-message">
                        If using decoupled flow, refresh the page after approving the consent.
                    </div>
                </div>
            )}

            {consentId && (
                <div className="consent-info">
                    <strong>Consent ID:</strong> {consentId}
                    {selectedAccountId && (
                        <>
                            <br />
                            <strong>Selected Account ID:</strong> {selectedAccountId}
                        </>
                    )}
                </div>
            )}

            <div className="tabs-container">
                <div className="tabs-header">
                    <button
                        className={`tab-btn ${activeTab === 'accounts' ? 'active' : ''}`}
                        onClick={() => handleTabClick('accounts')}
                    >
                        Accounts
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'balances' ? 'active' : ''} ${!selectedAccountId ? 'disabled' : ''}`}
                        onClick={() => handleTabClick('balances')}
                        disabled={!selectedAccountId}
                    >
                        Balances
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'party' ? 'active' : ''} ${!selectedAccountId ? 'disabled' : ''}`}
                        onClick={() => handleTabClick('party')}
                        disabled={!selectedAccountId}
                    >
                        Party
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'statements' ? 'active' : ''} ${!selectedAccountId ? 'disabled' : ''}`}
                        onClick={() => handleTabClick('statements')}
                        disabled={!selectedAccountId}
                    >
                        Statements
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'transactions' ? 'active' : ''} ${!selectedAccountId ? 'disabled' : ''}`}
                        onClick={() => handleTabClick('transactions')}
                        disabled={!selectedAccountId}
                    >
                        Transactions
                    </button>
                </div>

                <div className="tab-content">
                    {activeTab === 'accounts' && renderAccountsTab()}
                    {activeTab === 'balances' && renderBalancesTab()}
                    {activeTab === 'party' && renderPartyTab()}
                    {activeTab === 'statements' && renderStatementsTab()}
                    {activeTab === 'transactions' && renderTransactionsTab()}
                </div>
            </div>

            <style>{`
                .redirect-container {
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 20px;
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
                }

                .redirect-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 30px;
                    padding-bottom: 20px;
                    border-bottom: 2px solid #e0e0e0;
                }

                .redirect-header h1 {
                    margin: 0;
                    color: #333;
                }

                .new-app-btn {
                    padding: 12px 24px;
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                    border: none;
                    border-radius: 8px;
                    font-size: 16px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }

                .new-app-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 5px 20px rgba(102, 126, 234, 0.4);
                }

                .error-message {
                    background: #f8d7da;
                    border: 1px solid #f5c6cb;
                    color: #721c24;
                    padding: 12px;
                    border-radius: 8px;
                    margin-bottom: 20px;
                }

                .info-message {
                    background: #d1ecf1;
                    border: 1px solid #bee5eb;
                    color: #0c5460;
                    padding: 10px;
                    border-radius: 6px;
                    margin-top: 10px;
                    font-size: 14px;
                }

                .consent-info {
                    background: #d4edda;
                    border: 1px solid #c3e6cb;
                    color: #155724;
                    padding: 12px;
                    border-radius: 8px;
                    margin-bottom: 20px;
                }

                .tabs-container {
                    background: white;
                    border-radius: 12px;
                    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
                    overflow: hidden;
                }

                .tabs-header {
                    display: flex;
                    background: #f8f9fa;
                    border-bottom: 2px solid #e0e0e0;
                }

                .tab-btn {
                    flex: 1;
                    padding: 15px;
                    background: none;
                    border: none;
                    font-size: 16px;
                    font-weight: 500;
                    color: #666;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    position: relative;
                }

                .tab-btn:hover:not(.disabled) {
                    background: #e9ecef;
                    color: #333;
                }

                .tab-btn.active {
                    color: #667eea;
                    background: white;
                }

                .tab-btn.active::after {
                    content: '';
                    position: absolute;
                    bottom: -2px;
                    left: 0;
                    right: 0;
                    height: 2px;
                    background: #667eea;
                }

                .tab-btn.disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .tab-content {
                    padding: 30px;
                    min-height: 400px;
                }

                .accounts-list {
                    margin-bottom: 30px;
                }

                .accounts-list h3 {
                    color: #333;
                    margin-bottom: 20px;
                }

                .account-option {
                    display: flex;
                    align-items: center;
                    padding: 15px;
                    margin-bottom: 10px;
                    border: 2px solid #e0e0e0;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }

                .account-option:hover {
                    border-color: #667eea;
                    background: #f8f9fa;
                }

                .account-option input[type="radio"] {
                    margin-right: 15px;
                    width: 20px;
                    height: 20px;
                }

                .account-info {
                    display: flex;
                    flex-direction: column;
                    gap: 5px;
                }

                .account-number {
                    font-weight: 600;
                    color: #333;
                }

                .account-name {
                    color: #666;
                }

                .account-currency {
                    color: #999;
                    font-size: 14px;
                }

                .account-details {
                    background: #f8f9fa;
                    border: 1px solid #e0e0e0;
                    border-radius: 8px;
                    padding: 20px;
                }

                .account-details h3 {
                    color: #333;
                    margin-bottom: 15px;
                }

                .statements-list {
                    margin-bottom: 30px;
                }

                .statement-option {
                    display: flex;
                    align-items: center;
                    padding: 15px;
                    margin-bottom: 10px;
                    border: 2px solid #e0e0e0;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }

                .statement-option:hover {
                    border-color: #667eea;
                    background: #f8f9fa;
                }

                .statement-option input[type="radio"] {
                    margin-right: 15px;
                    width: 20px;
                    height: 20px;
                }

                .statement-info {
                    display: flex;
                    flex-direction: column;
                    gap: 5px;
                    width: 100%;
                }

                .statement-id {
                    font-weight: 600;
                    color: #333;
                }

                .statement-type {
                    color: #666;
                }

                .statement-date {
                    color: #999;
                    font-size: 14px;
                }

                .statement-period {
                    color: #999;
                    font-size: 14px;
                }

                .statement-details {
                    background: #f8f9fa;
                    border: 1px solid #e0e0e0;
                    border-radius: 8px;
                    padding: 20px;
                }

                .statement-details-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 15px;
                }

                .statement-details h3 {
                    color: #333;
                    margin: 0;
                }

                .download-statement-btn {
                    padding: 10px 20px;
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                    border: none;
                    border-radius: 6px;
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }

                .download-statement-btn:hover:not(:disabled) {
                    transform: translateY(-2px);
                    box-shadow: 0 5px 20px rgba(102, 126, 234, 0.4);
                }

                .download-statement-btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                    transform: none;
                }

                .details-content {
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                }

                .detail-row {
                    display: flex;
                    align-items: flex-start;
                    padding: 10px 0;
                    border-bottom: 1px solid #f0f0f0;
                }

                .detail-row:last-child {
                    border-bottom: none;
                }

                .detail-label {
                    font-weight: 600;
                    color: #555;
                    min-width: 180px;
                    flex-shrink: 0;
                }

                .detail-value {
                    color: #333;
                    flex: 1;
                    text-align: left;
                    word-break: break-word;
                }

                .table-container {
                    overflow-x: auto;
                    margin-top: 20px;
                }

                .data-table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 14px;
                }

                .data-table th {
                    background: #f8f9fa;
                    color: #333;
                    font-weight: 600;
                    padding: 12px;
                    text-align: left;
                    border-bottom: 2px solid #e0e0e0;
                    white-space: nowrap;
                }

                .data-table td {
                    padding: 10px 12px;
                    border-bottom: 1px solid #f0f0f0;
                    color: #555;
                }

                .data-table tr:hover {
                    background: #f8f9fa;
                }

                .data-table .amount {
                    text-align: right;
                    font-family: monospace;
                }

                .data-table .amount.debit {
                    color: #dc3545;
                }

                .data-table .amount.credit {
                    color: #28a745;
                }

                pre {
                    background: #f8f9fa;
                    border: 1px solid #e0e0e0;
                    border-radius: 8px;
                    padding: 15px;
                    overflow-x: auto;
                    font-size: 14px;
                    line-height: 1.5;
                }

                @media (max-width: 768px) {
                    .redirect-header {
                        flex-direction: column;
                        gap: 15px;
                    }

                    .tabs-header {
                        flex-direction: column;
                    }

                    .tab-btn {
                        text-align: left;
                        padding: 12px 20px;
                    }

                    .tab-btn.active::after {
                        left: 0;
                        right: auto;
                        top: 0;
                        bottom: 0;
                        width: 3px;
                        height: auto;
                    }
                }
            `}</style>
        </div>
    );
};

export default Redirect;